import { MapPin, Loader2 } from 'lucide-react';
import React, { useState, useMemo, useCallback } from 'react';

import { AddressForm } from '../../../components/forms/AddressForm';
import { AddressData } from '../../../lib/utils/geocoding';
import { useOnboardingStore } from '../../../store/onboarding.store';
import { saveCompanyData } from '../../../utils/saveCompanyData';

interface AddressStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function AddressStep({ onNext, onBack }: AddressStepProps) {
  const { companyData, setCompanyData, companyId, setCompanyId } = useOnboardingStore();
  const [isValid, setIsValid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddressValidate = useCallback((valid: boolean, addressData?: AddressData) => {
    setIsValid(valid);
    if (valid && addressData) {
      // Update store with address data
      setCompanyData({
        street: addressData.street || '',
        street2: addressData.unit || '',
        city: addressData.city || '',
        state: addressData.state || '',
        zip: addressData.zip || '',
        latitude: addressData.latitude,
        longitude: addressData.longitude,
        place_id: addressData.place_id,
        business_name: addressData.business_name,
      });
    }
  }, [setCompanyData]);

  const canContinue = isValid && 
    companyData.street && 
    companyData.city && 
    companyData.state && 
    companyData.zip &&
    companyData.latitude &&
    companyData.longitude;

  const handleContinue = async () => {
    if (!canContinue) return;

    setSaving(true);
    setError(null);

    try {
      // Save incrementally - UPDATE if companyId exists, CREATE if not
      const result = await saveCompanyData(companyId, {
        name: companyData.name,
        street: companyData.street || null,
        street2: companyData.street2 || null,
        city: companyData.city || null,
        state: companyData.state || null,
        zip: companyData.zip || null,
        latitude: companyData.latitude || null,
        longitude: companyData.longitude || null,
        place_id: companyData.place_id || null,
      });

      // Store companyId if it was just created
      if (result.created && result.companyId) {
        setCompanyId(result.companyId);
      }

      onNext();
    } catch (err) {
      console.error('[AddressStep] Save error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Memoize initial data to prevent unnecessary re-renders
  const initialAddressData: Partial<AddressData> = useMemo(() => ({
    street: companyData.street,
    unit: companyData.street2,
    city: companyData.city,
    state: companyData.state,
    zip: companyData.zip,
    latitude: companyData.latitude,
    longitude: companyData.longitude,
    place_id: companyData.place_id,
    business_name: companyData.business_name,
  }), [
    companyData.street,
    companyData.street2,
    companyData.city,
    companyData.state,
    companyData.zip,
    companyData.latitude,
    companyData.longitude,
    companyData.place_id,
    companyData.business_name,
  ]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
          <MapPin size={32} className="text-rollops-accent" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Primary Business Address</h2>
        <p className="text-[#A9A3A3] mt-2">Primary business entity address for billing and tax reporting</p>
        <p className="text-xs text-[#706A6A] mt-1">Note: Gym location addresses will be collected in the Location section</p>
      </div>

      <AddressForm
        initialData={initialAddressData}
        onValidate={handleAddressValidate}
        placeholder="Enter address or business name..."
        requiredFields={['street', 'city', 'state', 'zip']}
      />

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
          <p className="text-red-400 text-sm text-center">{error}</p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          disabled={saving}
          className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleContinue}
          disabled={!canContinue || saving}
          className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Saving...
            </>
          ) : (
            'Continue'
          )}
        </button>
      </div>
    </div>
  );
}
